import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { languageInUse } from '../../i18n/languages';

import { sidebarQueryOptions } from '../queryOptions';
import { useCanVisit } from '../routing/canVisit';

import { visibleItems } from './sidebarItems';
import type { SidebarItem } from './sidebarItems';

export type SidebarState =
  | { readonly status: 'ready'; readonly items: readonly SidebarItem[] }
  | { readonly status: 'loading' }
  | { readonly status: 'failed'; readonly retry: () => void };

const NO_ITEMS: readonly SidebarItem[] = [];

const LOADING: SidebarState = { status: 'loading' };


export const useSidebarState = (): SidebarState => {
  const { i18n } = useTranslation();
  // Links do not change with the language, so the last menu stays up until the new one's labels arrive.
  const { data, status, refetch } = useQuery({ ...sidebarQueryOptions(languageInUse(i18n)), placeholderData: keepPreviousData });
  const canVisit = useCanVisit();
  const items = useMemo(() => (data === undefined ? undefined : visibleItems(data, canVisit)), [data, canVisit]);
  if (items !== undefined) return { status: 'ready', items };
  if (status === 'error') return { status: 'failed', retry: () => void refetch() };
  return LOADING;
};

export const useSidebarItems = (): readonly SidebarItem[] => {
  const sidebar = useSidebarState();
  return sidebar.status === 'ready' ? sidebar.items : NO_ITEMS;
};
